import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list with "View More" batching
 */
export default class BlogPostsList extends PaginatedList {
  visibleCount = 0;
  isMobile = false;

  connectedCallback() {
    super.connectedCallback();
    this.isMobile = window.innerWidth < 750;
    this.visibleCount = this.isMobile ? 6 : 10;
    
    this.#initViewMore();
    
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 750;
    });
  }

  #initViewMore() {
    const viewMoreButton = this.#getRef('viewMoreButton');
    if (!viewMoreButton) return;

    viewMoreButton.addEventListener('click', () => this.#handleViewMoreClick());
    this.#updateVisibility();
  }

  #getRef(name) {
    const ref = this.refs[name];
    if (Array.isArray(ref)) return ref[0];
    return ref;
  }

  get allCards() {
    // Re-query cards to ensure we have any newly added ones
    return Array.from(this.querySelectorAll('[ref="cards[]"]'));
  }

  #updateVisibility() {
    const cards = this.allCards;
    const viewMoreButton = this.#getRef('viewMoreButton');
    const grid = this.#getRef('grid');
    
    if (!viewMoreButton) return;

    cards.forEach((card, index) => {
      if (index < this.visibleCount) {
        card.classList.remove('is-hidden');
      } else {
        card.classList.add('is-hidden');
      }
    });

    const hasHiddenCards = cards.some(card => card.classList.contains('is-hidden'));
    const currentPageCount = this.#getCurrentMaxPage();
    const lastPage = grid ? Number(grid.dataset.lastPage) : 1;
    const hasMorePages = lastPage > currentPageCount;

    if (hasHiddenCards || hasMorePages) {
      viewMoreButton.classList.remove('is-hidden');
    } else {
      viewMoreButton.classList.add('is-hidden');
    }
  }

  #getCurrentMaxPage() {
    const cards = this.allCards;
    if (cards.length === 0) return 1;
    const pages = cards.map(card => Number(card.dataset.page)).filter(page => !isNaN(page));
    return pages.length > 0 ? Math.max(...pages) : 1;
  }

  async #handleViewMoreClick() {
    const viewMoreButton = this.#getRef('viewMoreButton');
    const grid = this.#getRef('grid');
    const batchSize = this.isMobile ? 4 : 6;
    
    let cards = this.allCards;
    let hiddenCards = cards.filter(card => card.classList.contains('is-hidden'));

    if (hiddenCards.length > 0) {
      this.visibleCount += batchSize;
      this.#updateVisibility();
    } else {
      // Need to fetch next page
      const lastPage = grid ? Number(grid.dataset.lastPage) : 1;
      const hasMorePages = lastPage > this.#getCurrentMaxPage();
      
      if (hasMorePages) {
        if (viewMoreButton) viewMoreButton.classList.add('loading');
        await this.renderNextPage();
        if (viewMoreButton) viewMoreButton.classList.remove('loading');
        
        // Increment visible count and update visibility
        this.visibleCount += batchSize;
        this.#updateVisibility();
      }
    }
  }

  observeViewMore() {
    // Disable default infinite scroll observer
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
